import { useState, useCallback, useEffect, useRef, type MutableRefObject } from "react";
import { logEvent } from "../../../../../telemetry";
import { buildPaletteGrid } from "../../../utils/colourUtils";
import { validateSelection } from "../../../utils/validateSelection";
import {
  extractThemeColors,
  applyFillColour,
  applyFontColour,
  applyLineColour,
  applyNoFill,
  applyNoLine,
  applyOnBrandFill,
  shuffleColour,
  detectNonBrandColours,
  getSelectedShapeColours,
  detectColoursInScope,
  replaceColours,
  cleanUpStyling,
  getSlideCount,
  REPLACE_SELECTION_REQUIRED_MESSAGE,
  getUnsupportedSelectionMessage,
} from "../services/colourOps";
import type {
  ThemeColors,
  PaletteCell,
  ColourTarget,
  NonBrandResult,
  CleanUpResult,
  ShapeColorEntry,
  ColourToolsState,
  ColourToolsActions,
} from "../../../types";

const HOOK_CONNECTOR = "deckorate";

// Guards one async "channel" (theme load / busy actions / non-brand detect /
// selected-colour detect all have their own) against re-entry: a second call
// started while the first is still in flight is a no-op instead of running
// concurrently — this is what keeps a fast double-click/double-invoke from
// slipping a second Office.js call in before `tools.busy` etc. re-render to
// disable the triggering button. The no-op is logged (not silent) so a
// dropped call is visible in telemetry instead of looking identical to
// "nothing was clicked" — `label` identifies which action was dropped, even
// though several actions share one ref (e.g. every busy-channel action).
async function guardAsync(
  label: string,
  inFlightRef: MutableRefObject<boolean>,
  body: () => Promise<void>,
): Promise<void> {
  if (inFlightRef.current) {
    logEvent("connector.action.invoke", "warn", {
      connectorSystemName: HOOK_CONNECTOR,
      reason: `${label}-concurrent-call-dropped`,
    });
    return;
  }
  inFlightRef.current = true;
  try {
    await body();
  } finally {
    inFlightRef.current = false;
  }
}

export function useColourTools(): ColourToolsState & ColourToolsActions {
  const [themeColors, setThemeColors] = useState<ThemeColors | null>(null);
  const [palette, setPalette] = useState<PaletteCell[][]>([]);
  const [themeLoading, setThemeLoading] = useState(false);
  const [themeError, setThemeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [nonBrandResults, setNonBrandResults] = useState<NonBrandResult[]>([]);
  const [detecting, setDetecting] = useState(false);
  const [detectProgress, setDetectProgress] = useState(0);
  const [replaceDone, setReplaceDone] = useState<number | null>(null);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const [cleanUpResult, setCleanUpResult] = useState<CleanUpResult | null>(null);
  const [selectedShapeColors, setSelectedShapeColors] = useState<ShapeColorEntry[]>([]);
  const [detectingShapeColors, setDetectingShapeColors] = useState(false);
  const [slideCount, setSlideCount] = useState<number | null>(null);

  // Tracks whether the component that owns this hook instance is still
  // mounted, so a slow-resolving Office.js call can't write state into a
  // view the user has already navigated away from. (React 18 already no-ops
  // a setState on an unmounted component rather than warning/leaking, so
  // this isn't guarding against a crash — it's guarding against a *stale*
  // result silently overwriting whatever the next view renders, and against
  // a spinner/status that never gets cleared because its owner is gone.)
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Every failure message auto-dismisses after a few seconds instead of
  // lingering until the user's next action — a stale error still on screen
  // after the user has already moved on (e.g. tried something else and
  // succeeded) reads as an ongoing problem even once it no longer applies.
  const ERROR_NOTICE_DURATION_MS = 5000;
  const errorTimeoutRef = useRef<number | null>(null);

  const clearErrorTimeout = useCallback(() => {
    if (errorTimeoutRef.current != null) {
      window.clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
  }, []);
  useEffect(() => clearErrorTimeout, [clearErrorTimeout]);

  // Replaces every `setError(message)` call below — same message, but with
  // an auto-dismiss timer attached (and any previous timer cleared first,
  // so an old error can't out-live the new one it's being replaced by).
  const reportError = useCallback((message: string) => {
    clearErrorTimeout();
    if (isMountedRef.current) setError(message);
    errorTimeoutRef.current = window.setTimeout(() => {
      if (isMountedRef.current) setError(null);
      errorTimeoutRef.current = null;
    }, ERROR_NOTICE_DURATION_MS);
  }, [clearErrorTimeout]);

  // Replaces every `setError(null)` call below (both the "clear before
  // starting a new action" calls and the manually-triggered clearError
  // action) — also cancels any pending auto-dismiss timer so it can't fire
  // later and clear a completely unrelated, newer error.
  const clearErrorNow = useCallback(() => {
    clearErrorTimeout();
    setError(null);
  }, [clearErrorTimeout]);

  // Same auto-dismiss treatment as error, applied to the success/status
  // message — "Replaced 1 colour instance(s)." used to persist forever
  // (nothing ever cleared it except another action's own setStatus call),
  // so it kept showing on the main panel after closing whichever modal
  // produced it, and stayed stuck through a later, unrelated action that
  // never happened to set its own status.
  const STATUS_NOTICE_DURATION_MS = 5000;
  const statusTimeoutRef = useRef<number | null>(null);

  const clearStatusTimeout = useCallback(() => {
    if (statusTimeoutRef.current != null) {
      window.clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = null;
    }
  }, []);
  useEffect(() => clearStatusTimeout, [clearStatusTimeout]);

  const reportStatus = useCallback((message: string) => {
    clearStatusTimeout();
    if (isMountedRef.current) setStatus(message);
    statusTimeoutRef.current = window.setTimeout(() => {
      if (isMountedRef.current) setStatus(null);
      statusTimeoutRef.current = null;
    }, STATUS_NOTICE_DURATION_MS);
  }, [clearStatusTimeout]);

  const clearStatusNow = useCallback(() => {
    clearStatusTimeout();
    setStatus(null);
  }, [clearStatusTimeout]);

  // One re-entry guard per independent "busy channel" — `busy` is shared by
  // every apply/clear/shuffle/on-brand/replace/clean-up action (they already
  // share one UI spinner), while theme-load and the two detect flows each
  // have their own.
  const busyRef = useRef(false);
  const themeLoadingRef = useRef(false);
  const detectingRef = useRef(false);
  const detectingShapeColorsRef = useRef(false);
  const slideCountLoadingRef = useRef(false);

  const loadSlideCount = useCallback(async () => {
    await guardAsync("load-slide-count", slideCountLoadingRef, async () => {
      const count = await getSlideCount();
      if (isMountedRef.current) setSlideCount(count);
    });
  }, []);

  const loadTheme = useCallback(async () => {
    await guardAsync("load-theme", themeLoadingRef, async () => {
      setThemeLoading(true);
      setThemeError(null);
      try {
        const colors = await extractThemeColors();
        if (isMountedRef.current) {
          setThemeColors(colors);
          setPalette(buildPaletteGrid(colors));
        }
      } catch (e) {
        // extractThemeColors already reports failures through the shared
        // telemetry pipeline (with type + scrubbed message) before falling
        // back to FALLBACK_THEME — this catch only turns that into
        // user-visible state, so it does not log again here.
        if (isMountedRef.current) setThemeError(e instanceof Error ? e.message : String(e));
      } finally {
        if (isMountedRef.current) setThemeLoading(false);
      }
    });
  }, []);

  const applyFill = useCallback(async (hex: string) => {
    await guardAsync("apply-fill", busyRef, async () => {
      setBusy(true);
      clearErrorNow();
      clearStatusNow();
      try {
        await applyFillColour(hex);
        reportStatus("Fill colour applied.");
      } catch (e) {
        // colourOps already logs the underlying failure (operation, error
        // type, scrubbed message) before rethrowing — surfacing it here.
        reportError(e instanceof Error ? e.message : String(e));
      } finally {
        if (isMountedRef.current) setBusy(false);
      }
    });
  }, []);

  const applyFont = useCallback(async (hex: string) => {
    await guardAsync("apply-font", busyRef, async () => {
      setBusy(true);
      clearErrorNow();
      clearStatusNow();
      try {
        await applyFontColour(hex);
        reportStatus("Font colour applied.");
      } catch (e) {
        // colourOps already logs the underlying failure (operation, error
        // type, scrubbed message) before rethrowing — surfacing it here.
        reportError(e instanceof Error ? e.message : String(e));
      } finally {
        if (isMountedRef.current) setBusy(false);
      }
    });
  }, []);

  const applyLine = useCallback(async (hex: string) => {
    await guardAsync("apply-line", busyRef, async () => {
      setBusy(true);
      clearErrorNow();
      clearStatusNow();
      try {
        await applyLineColour(hex);
        reportStatus("Line colour applied.");
      } catch (e) {
        // colourOps already logs the underlying failure (operation, error
        // type, scrubbed message) before rethrowing — surfacing it here.
        reportError(e instanceof Error ? e.message : String(e));
      } finally {
        if (isMountedRef.current) setBusy(false);
      }
    });
  }, []);

  const clearFill = useCallback(async () => {
    await guardAsync("clear-fill", busyRef, async () => {
      setBusy(true);
      clearErrorNow();
      clearStatusNow();
      try {
        await applyNoFill();
        reportStatus("Fill removed.");
      } catch (e) {
        // colourOps already logs the underlying failure (operation, error
        // type, scrubbed message) before rethrowing — surfacing it here.
        reportError(e instanceof Error ? e.message : String(e));
      } finally {
        if (isMountedRef.current) setBusy(false);
      }
    });
  }, []);

  const clearLine = useCallback(async () => {
    await guardAsync("clear-line", busyRef, async () => {
      setBusy(true);
      clearErrorNow();
      clearStatusNow();
      try {
        await applyNoLine();
        reportStatus("Line removed.");
      } catch (e) {
        // colourOps already logs the underlying failure (operation, error
        // type, scrubbed message) before rethrowing — surfacing it here.
        reportError(e instanceof Error ? e.message : String(e));
      } finally {
        if (isMountedRef.current) setBusy(false);
      }
    });
  }, []);

  const onBrandFill = useCallback(async () => {
    await guardAsync("on-brand-fill", busyRef, async () => {
      setBusy(true);
      clearErrorNow();
      clearStatusNow();
      try {
        await applyOnBrandFill(themeColors);
        reportStatus("On-brand fill applied.");
      } catch (e) {
        // colourOps already logs the underlying failure (operation, error
        // type, scrubbed message) before rethrowing — surfacing it here.
        reportError(e instanceof Error ? e.message : String(e));
      } finally {
        if (isMountedRef.current) setBusy(false);
      }
    });
  }, [themeColors]);

  const shuffle = useCallback(async (target: ColourTarget = "fill") => {
    await guardAsync("shuffle", busyRef, async () => {
      setBusy(true);
      clearErrorNow();
      clearStatusNow();
      try {
        await shuffleColour(themeColors, target);
        reportStatus("Colour shuffled.");
      } catch (e) {
        // colourOps already logs the underlying failure (operation, error
        // type, scrubbed message) before rethrowing — surfacing it here.
        reportError(e instanceof Error ? e.message : String(e));
      } finally {
        if (isMountedRef.current) setBusy(false);
      }
    });
  }, [themeColors]);

  const detectNonBrand = useCallback(
    async (
      slideFrom?: number,
      slideTo?: number,
      threshold?: number,
      currentSlideOnly = false,
      target: ColourTarget = "fill",
      selectedShapesOnly = false,
    ) => {
      await guardAsync("detect-non-brand", detectingRef, async () => {
        setDetecting(true);
        setDetectProgress(0);
        setNonBrandResults([]);
        clearErrorNow();
        clearStatusNow();
        try {
          const results = await detectNonBrandColours(themeColors, {
            slideFrom,
            slideTo,
            threshold,
            onProgress: (progress: number) => {
              if (isMountedRef.current) setDetectProgress(progress);
            },
            currentSlideOnly,
            target,
            selectedShapesOnly,
          });
          if (isMountedRef.current) {
            setNonBrandResults(results);
            reportStatus(`Found ${results.length} non-brand colour(s).`);
          }
        } catch (e) {
          // detectNonBrandColours never rejects — it already logs failures
          // internally and resolves with a safe fallback (empty array)
          // instead. This catch is a defensive backstop only, in case that
          // contract changes in the future; it is not expected to be
          // reached today.
          reportError(e instanceof Error ? e.message : String(e));
        } finally {
          if (isMountedRef.current) {
            setDetecting(false);
            setDetectProgress(100);
          }
        }
      });
    },
    [themeColors],
  );

  const detectSelectedColors = useCallback(
    async (
      colorType: ColourTarget,
      scope?: {
        slideFrom?: number;
        slideTo?: number;
        currentSlideOnly?: boolean;
        selectedShapesOnly?: boolean;
      },
    ) => {
      await guardAsync("detect-selected-colors", detectingShapeColorsRef, async () => {
        setDetectingShapeColors(true);
        try {
          // With no scope, or scope explicitly restricted to the literal
          // selection, fall back to the plain "current selection" detector —
          // detectColoursInScope's own selectedShapesOnly branch does the
          // same thing, but this keeps the common case's Office.js call
          // count unchanged for callers that never pass a scope at all.
          const colors = !scope || scope.selectedShapesOnly
            ? await getSelectedShapeColours(colorType)
            : await detectColoursInScope(colorType, scope);
          if (isMountedRef.current) setSelectedShapeColors(colors);
        } catch (e) {
          // getSelectedShapeColours / detectColoursInScope never reject —
          // they already log failures internally and resolve with a safe
          // fallback (empty array) instead. This catch is a defensive
          // backstop only, in case that contract changes in the future; it
          // is not expected to be reached today.
          reportError(e instanceof Error ? e.message : String(e));
        } finally {
          if (isMountedRef.current) setDetectingShapeColors(false);
        }
      });
    },
    [],
  );

  const replace = useCallback(
    async (
      mappings: Array<{ from: string; to: string }>,
      targets: Set<ColourTarget>,
      slideFrom?: number,
      slideTo?: number,
      currentSlideOnly = false,
      selectedShapesOnly = false,
    ) => {
      await guardAsync("replace", busyRef, async () => {
        setBusy(true);
        clearErrorNow();
        clearStatusNow();
        setReplaceDone(null);
        setReplaceProgress(0);
        try {
          const count = await replaceColours(
            mappings,
            targets,
            slideFrom,
            slideTo,
            (current, total) => {
              if (isMountedRef.current) setReplaceProgress(Math.round((current / total) * 100));
            },
            currentSlideOnly,
            selectedShapesOnly,
          );
          if (isMountedRef.current) {
            setReplaceDone(count);
            setReplaceProgress(100);
            reportStatus(`Replaced ${count} colour instance(s).`);
          }
        } catch (e) {
          // replaceColours resolves with a safe fallback (0) for every
          // internal failure except one: the "Selected Shapes" scope with
          // nothing selected, which it deliberately rethrows (already
          // logged) so this reaches the user instead of silently reporting
          // "Replaced 0 instance(s)." with no explanation.
          reportError(e instanceof Error ? e.message : String(e));
        } finally {
          if (isMountedRef.current) setBusy(false);
        }
      });
    },
    [],
  );

  // Gates opening the Replace Colors modal on a literal selection existing
  // up front — unlike replace() itself, this runs before any scope has even
  // been chosen, so it can't rely on `selectedShapesOnly`; it's a flat
  // "is anything selected right now" check every time the caller asks.
  // Also used to re-check the live selection while the modal stays open
  // (see ReplaceColorsModal's polling effect) — Office.js has no
  // selection-changed event to react to instead.
  //
  // Now safe to set `error` directly on failure (previously deliberately
  // avoided, since `error` used to persist until the next action — a stale
  // "please select a shape" could out-live a later, successful check). Now
  // that every error auto-dismisses via reportError/clearErrorNow, a
  // successful check simply clears it immediately instead, so there's no
  // "remember to clear a shared field" risk anymore — this also means
  // opening Replace successfully clears out any unrelated stale error
  // (e.g. from a prior Shuffle failure) instead of showing both at once.
  const requireSelectionForReplace = useCallback(async (): Promise<boolean> => {
    if (!(await validateSelection())) {
      reportError(REPLACE_SELECTION_REQUIRED_MESSAGE);
      return false;
    }
    // Charts/pictures previously fell through silently here — the modal
    // opened anyway and just showed nothing detected, with no explanation.
    const unsupported = await getUnsupportedSelectionMessage("Replace Colors");
    if (unsupported) {
      reportError(unsupported);
      return false;
    }
    clearErrorNow();
    clearStatusNow();
    return true;
  }, [clearErrorNow, clearStatusNow, reportError]);

  // Same "is the live selection usable" check as requireSelectionForReplace,
  // minus the "must have something selected" half — Non-Brand's "Selected
  // Shapes" scope is used the same way, but (like the rest of Non-Brand
  // detection) doesn't itself require a selection to exist; it only needs
  // to reject a chart/picture if one happens to be selected instead of
  // silently scanning it and finding nothing.
  const requireSupportedSelection = useCallback(async (action: string): Promise<boolean> => {
    const unsupported = await getUnsupportedSelectionMessage(action);
    if (unsupported) {
      reportError(unsupported);
      return false;
    }
    clearErrorNow();
    return true;
  }, [clearErrorNow, reportError]);

  const cleanUp = useCallback(async () => {
    await guardAsync("clean-up", busyRef, async () => {
      setBusy(true);
      clearErrorNow();
      clearStatusNow();
      setCleanUpResult(null);
      try {
        const result = await cleanUpStyling();
        if (isMountedRef.current) {
          setCleanUpResult(result);
          reportStatus("Clean up complete.");
        }
      } catch (e) {
        // cleanUpStyling never rejects — it already logs failures
        // internally and resolves with a safe fallback (CleanUpResult with
        // shapesFixed: 0) instead. This catch is a defensive backstop only,
        // in case that contract changes in the future; it is not expected
        // to be reached today.
        reportError(e instanceof Error ? e.message : String(e));
      } finally {
        if (isMountedRef.current) setBusy(false);
      }
    });
  }, []);

  // Derived, not stored — always reflects whichever flag is actually true
  // this render, so it can never drift out of sync with themeLoading/busy.
  let loadingLabel: string | null;
  if (themeLoading) {
    loadingLabel = "Loading theme…";
  } else if (busy) {
    loadingLabel = "Applying…";
  } else {
    loadingLabel = null;
  }

  const clearStatus = useCallback(() => clearStatusNow(), [clearStatusNow]);
  const clearError = useCallback(() => clearErrorNow(), [clearErrorNow]);
  const clearNonBrandResults = useCallback(() => setNonBrandResults([]), []);
  const clearCleanUpResult = useCallback(() => setCleanUpResult(null), []);

  return {
    // state
    themeColors,
    palette,
    themeLoading,
    themeError,
    busy,
    error,
    status,
    nonBrandResults,
    detecting,
    detectProgress,
    replaceDone,
    replaceProgress,
    cleanUpResult,
    selectedShapeColors,
    detectingShapeColors,
    slideCount,
    loadingLabel,
    // actions
    loadTheme,
    loadSlideCount,
    applyFill,
    applyFont,
    applyLine,
    clearFill,
    clearLine,
    onBrandFill,
    shuffle,
    detectNonBrand,
    detectSelectedColors,
    replace,
    requireSelectionForReplace,
    requireSupportedSelection,
    cleanUp,
    clearStatus,
    clearError,
    clearNonBrandResults,
    clearCleanUpResult,
  };
}
